import { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18n } from '../i18n/useI18n'
import { useStaffAuth } from '../store/useStaffAuth'
import { useMerchantTerms } from '../hooks/useMerchantTerms'
import { ApiError } from '../services/api'
import MerchantTermsBody from './MerchantTermsBody'

/**
 * 加盟店規約の未同意ブロック (#1657)。
 *
 * 電子契約 (MF クラウド契約) は「改定のたびに送付 → 押さない店が残る → 新旧規約が
 * 混在する」運用リスクがあるため、同意はアプリ内で完結させる。規約は 1% 課金開始
 * (#516)・チャージバック条項など**何度も変わる**ので、版が上がるたびに再同意を取る。
 *
 * ブロックしない相手:
 * - **スタッフ** — オーナーの代わりに契約へ同意はできない。現場が止まるだけ
 * - `/store/terms` — 規約を読む画面自体を塞ぐと同意にたどり着けない
 * - 取得失敗時 — 規約 API の障害で店舗のレジが止まると営業できなくなる
 */
export default function StoreTermsGate({ children }: { children: React.ReactNode }) {
  const { t } = useI18n()
  const location = useLocation()
  const staffSession = useStaffAuth((s) => s.session)
  const { terms, loading, agree, reload } = useMerchantTerms()
  const [submitting, setSubmitting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [stale, setStale] = useState(false)
  const [scrolledToEnd, setScrolledToEnd] = useState(false)
  // #1736 改定後は変更点を先に見せ、全文は折りたたむ (読ませる必要があるときは開いたまま)
  const [fullTextOpen, setFullTextOpen] = useState(false)
  const bodyRef = useRef<HTMLDivElement | null>(null)

  if (staffSession) return <>{children}</>
  if (location.pathname === '/store/terms') return <>{children}</>
  // **読み込み中は children を出さない。** 出すと、未同意のオーナーが一瞬
  // 操作可能な画面に触れられ、直後にモーダルが被さる (画面遷移のたびにちらつく)。
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    )
  }
  if (!terms || !terms.needsAgreement) return <>{children}</>

  // 不利益変更 (新たな金銭負担を課す改定) は、継続利用によるみなし同意ではなく
  // **最後まで読んだ上での明示的なタップ**を求める。民法の定型約款の下では
  // 不利益変更のみなし同意が弱いため。
  const mustRead = terms.requiresExplicitConsent
  const canAgree = !submitting && (!mustRead || scrolledToEnd)
  const changes = terms.changesSinceAgreed ?? []
  const isRevision = !!terms.agreedVersion && changes.length > 0
  // 読ませるときは全文を閉じられない (閉じると「最後まで読んだ」が成立してしまう)
  const showFullText = !isRevision || mustRead || fullTextOpen

  // 変更内容の文から条番号を拾い、本文の見出しへ飛ぶ (「第 10 条」「10.8 条」「10.8「…」」)
  const jumpTo = (summary: string) => {
    const container = bodyRef.current
    if (!container) return
    const compact = summary.replace(/\s+/g, '')
    const targets: string[] = []
    for (const m of compact.matchAll(/(\d+\.\d+)条?/g)) targets.push(m[1])
    for (const m of compact.matchAll(/第(\d+)条/g)) targets.push(`第${m[1]}条`)
    const headings = Array.from(container.querySelectorAll<HTMLElement>('[data-heading]'))
    for (const tgt of targets) {
      const hit = headings.find((h) => (h.dataset.heading || '').startsWith(tgt))
      if (hit) {
        setFullTextOpen(true)
        // 折りたたみを開いた直後は描画を待つ
        window.setTimeout(() => hit.scrollIntoView({ block: 'start', behavior: 'smooth' }), 50)
        return
      }
    }
    setFullTextOpen(true)
  }

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true)
  }

  // スクロールしなくても全文が収まる短い規約では onScroll が発火せず、
  // **ボタンが永久に押せなくなる** (このモーダルには閉じる手段が無い)。
  // 表示した時点で読み切れているので、その場合は解錠する。
  const unlockIfFullyVisible = (el: HTMLDivElement | null) => {
    if (el && el.scrollHeight <= el.clientHeight + 24) setScrolledToEnd(true)
  }

  const onAgree = async () => {
    setSubmitting(true)
    setFailed(false)
    try {
      await agree()
    } catch (e) {
      // 409 = 表示中の版が古い (開きっぱなしのタブで改定を跨いだ)。**必ず読み直す** —
      // 読み直さないと同じ古い版を送り続け、モーダルから永久に抜けられない。
      // 通信エラーではないので、そちらのメッセージは出さず「新しい版に変わった」と伝える。
      if (e instanceof ApiError && e.status === 409) {
        setScrolledToEnd(false)
        setStale(true)
        await reload()
      } else {
        setFailed(true)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl bg-white sm:rounded-2xl">
        <div className="border-b border-gray-100 px-5 pb-3 pt-5">
          <p className="font-heading text-base font-bold text-text-primary">
            {terms.agreedVersion
              ? t('storeTerms.gate.titleUpdated')
              : t('storeTerms.gate.titleFirst')}
          </p>
          <p className="mt-1 font-body text-[13px] text-text-secondary">
            {!terms.agreedVersion
              ? t('storeTerms.gate.leadFirst')
              : mustRead ? t('storeTerms.gate.leadDisadvantageous') : t('storeTerms.gate.lead')}
          </p>
          <p className="mt-1 font-body text-[11px] text-text-tertiary">
            {t('storeTerms.version')}: {terms.currentVersion}
            {terms.agreedVersion ? ` （${t('storeTerms.agreedVersion')}: ${terms.agreedVersion}）` : ''}
          </p>
        </div>

        <div
          ref={(el) => { bodyRef.current = el; unlockIfFullyVisible(el) }}
          onScroll={onScroll}
          className="min-h-0 flex-1 overflow-y-auto px-5 py-3"
        >
          {isRevision && (
            <section className="mb-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <p className="font-heading text-sm font-bold text-text-primary">
                {t('storeTerms.gate.changesTitle', { from: terms.agreedVersion ?? '' })}
              </p>
              <ul className="mt-2 space-y-2">
                {changes.map((c) => (
                  <li key={c.version} className="rounded-lg bg-white p-2.5 shadow-sm">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-navy/10 px-2 py-0.5 font-body text-[11px] font-medium text-navy">{c.version}</span>
                      <span className="font-body text-[11px] text-text-tertiary">{c.date}</span>
                      {c.disadvantageous && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 font-body text-[11px] font-medium text-red-700">
                          {t('storeTerms.gate.changeDisadvantageous')}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-body text-[12px] leading-relaxed text-text-secondary">{c.summary}</p>
                    <button
                      type="button"
                      onClick={() => jumpTo(c.summary)}
                      className="mt-1 font-body text-[12px] font-medium text-navy underline"
                    >
                      {t('storeTerms.gate.jumpTo')}
                    </button>
                  </li>
                ))}
              </ul>
              {!mustRead && (
                <button
                  type="button"
                  onClick={() => setFullTextOpen((v) => !v)}
                  className="mt-3 w-full rounded-lg border border-gray-300 bg-white py-2 font-body text-[12px] font-medium text-text-secondary"
                >
                  {fullTextOpen ? t('storeTerms.gate.hideFullText') : t('storeTerms.gate.showFullText')}
                </button>
              )}
            </section>
          )}
          {showFullText && (
            <>
              {isRevision && (
                <p className="mb-1 font-heading text-sm font-bold text-text-primary">{t('storeTerms.gate.fullText')}</p>
              )}
              <MerchantTermsBody text={terms.text} />
            </>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 pb-5 pt-3">
          {failed && (
            <p className="mb-2 font-body text-[12px] text-yokoso-red">{t('storeTerms.gate.failed')}</p>
          )}
          {stale && (
            <p className="mb-2 font-body text-[12px] text-text-secondary">{t('storeTerms.gate.stale')}</p>
          )}
          {mustRead && !scrolledToEnd && (
            <p className="mb-2 font-body text-[12px] text-text-tertiary">
              {t('storeTerms.gate.scrollToEnd')}
            </p>
          )}
          <button
            onClick={() => void onAgree()}
            disabled={!canAgree}
            className="tap-effect w-full rounded-xl bg-navy py-3 font-body text-sm font-medium text-white disabled:opacity-40"
          >
            {submitting ? t('storeTerms.gate.submitting') : t('storeTerms.gate.agree')}
          </button>
          <p className="mt-2 text-center font-body text-[11px] text-text-tertiary">
            {t('storeTerms.gate.recordNote')}
          </p>
        </div>
      </div>
    </div>
  )
}
